import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import RefreshIcon from "@mui/icons-material/Refresh";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { SxProps, Theme } from "@mui/material/styles";
import {
  ETaxDocTypeGroup,
  ETaxDocumentStatus,
  ETaxDocumentStatusCounts,
  ETaxKtbDocumentStatus,
  ETaxStatus,
} from "../../types/e-tax.type";
import { numberWithCommas } from "../../utils";
import { COMPACT_QUERY } from "../../utils/hooks/useCompact";
import { STATUS_COLOR, STATUS_LABEL } from "./data";
import {
  DOC_TYPE_CHIP_COLOR,
  DOC_TYPE_LABEL,
  EXPIRED_STATUS_META,
  IN_PROGRESS_GROUP_LABEL,
  PAPER_STATUS_META,
  getBillStatusMeta,
  getEtaxStatusMeta,
} from "./documentMeta";

export const LOAD_ERROR_MESSAGE = "โหลดข้อมูลไม่สำเร็จ";

/**
 * A failed load, kept apart from "ไม่พบข้อมูล": an empty result is a fact about the period, a
 * failed request is not, and reading one as the other sends people looking for bills that are
 * there.
 */
export const LoadErrorState = ({
  onRetry,
  compact: dense = false,
}: {
  onRetry: () => void;
  compact?: boolean;
}) => (
  <Box
    display="flex"
    flexDirection={dense ? "row" : "column"}
    alignItems="center"
    justifyContent="center"
    gap={dense ? 1 : 1.5}
  >
    <Typography color="error" fontWeight={500} variant={dense ? "body2" : "body1"}>
      {LOAD_ERROR_MESSAGE}
    </Typography>
    <Button
      size="small"
      variant="outlined"
      startIcon={<RefreshIcon fontSize="small" />}
      onClick={onRetry}
    >
      ลองใหม่
    </Button>
  </Box>
);

const compact = "@media " + COMPACT_QUERY;

// Header-cell divider shared with the Sale Report Detail table
// (see components/Report/Details/ReportDetailsList.tsx) so the E-Tax tables
// match its column separators. Apply to every header cell except the last.
export const headCellSx: SxProps<Theme> = {
  borderRight: (theme) => `1px solid ${theme.palette.draft.main}`,
};

// Compact horizontal summary stat box: label on the left, value on the right,
// thin border, small height. Sized to its content (does NOT stretch to fill the
// row) so several boxes sit together with a small gap and the status breakdown
// can sit to their right. Used by the E-Tax detail / send / batch screens.
export const SummaryDetailCard = ({
  title,
  value,
}: {
  title: string;
  value: string;
}) => (
  <Box
    sx={{
      flex: "0 1 auto",
      minWidth: 200,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 3,
      border: (theme) => `1px solid ${theme.palette.primary.main}`,
      borderRadius: 2,
      px: 2,
      py: 0.75,
      [compact]: { px: 1.5, py: 0.5, minWidth: 160, gap: 1.5 },
    }}
  >
    <Typography
      variant="body2"
      color="primary"
      fontWeight={600}
      noWrap
      sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
    >
      {title}
    </Typography>
    <Typography
      variant="subtitle1"
      fontWeight={700}
      noWrap
      sx={{ flexShrink: 0 }}
    >
      {value}
    </Typography>
  </Box>
);

// Total document count + send-status breakdown shown to the right of the detail summary
// boxes. Every label and colour is pulled from getEtaxStatusMeta, so a figure here reads
// as the same word as the status chip on the rows it counts. A bucket the caller leaves
// out is not rendered — batch screens have no ค้างส่ง rows to count.
// The in-progress bucket also holds RECEIVE / SUBMIT_RD rows, whose chips say what they wait
// for, so it takes the group label its filter uses rather than one of those chip words.
const STATUS_COUNT_META: {
  key: keyof ETaxDocumentStatusCounts;
  label: string;
  color: string;
}[] = (
  [
    ["success", getEtaxStatusMeta("COMPLETED")],
    [
      "in_progress",
      { ...getEtaxStatusMeta("IN_PROGRESS"), label: IN_PROGRESS_GROUP_LABEL },
    ],
    ["pending", getEtaxStatusMeta(null)],
    ["failed", getEtaxStatusMeta("FAILED")],
    ["expired", EXPIRED_STATUS_META],
    ["paper", PAPER_STATUS_META],
  ] as const
).map(([key, meta]) => ({ key, label: meta.label, color: meta.color }));

/** The buckets this set of counts actually carries, in display order. */
const presentCounts = (counts: ETaxDocumentStatusCounts) =>
  STATUS_COUNT_META.filter(({ key }) => counts[key] !== undefined).map((meta) => ({
    ...meta,
    value: counts[meta.key] ?? 0,
  }));

export const ETaxStatusCountSummary = ({
  count,
  counts,
}: {
  count: number;
  counts: ETaxDocumentStatusCounts;
}) => (
  <Box
    display="flex"
    alignItems="center"
    gap={2.5}
    flexShrink={0}
    sx={{ ml: "auto", [compact]: { gap: 1.5, flexWrap: "wrap", ml: 0 } }}
  >
    <Typography variant="body2" color="text.secondary">
      จำนวน:{" "}
      <Box component="span" fontWeight={700} color="text.primary">
        {numberWithCommas(count)} รายการ
      </Box>
    </Typography>
    {presentCounts(counts).map(({ key, label, color, value }) => (
      <Box key={key} display="flex" alignItems="center" gap={0.75}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            bgcolor: color,
            flexShrink: 0,
          }}
        />
        <Typography variant="body2" color="text.secondary">
          {label}:{" "}
          <Box component="span" fontWeight={700} color="text.primary">
            {numberWithCommas(value)}
          </Box>
        </Typography>
      </Box>
    ))}
  </Box>
);

// Label + value stack shared by the dashboard summary cards and the send-confirm
// modal so the two stay visually identical.
export const AmountRow = ({
  label,
  value,
}: {
  label: string;
  value: string;
}) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="subtitle1" fontWeight={600}>
      {value}
    </Typography>
  </Box>
);

export const DocTypeChip = ({ type }: { type: ETaxDocTypeGroup }) => {
  const { color, bg } = DOC_TYPE_CHIP_COLOR[type];
  return (
    <Chip
      label={DOC_TYPE_LABEL[type]}
      size="small"
      sx={{
        color,
        bgcolor: bg,
        fontWeight: 500,
        borderRadius: "16px",
        height: 24,
      }}
    />
  );
};

const StatusDot = ({ label, color }: { label: string; color: string }) => (
  <Box display="flex" alignItems="center" gap={1}>
    <Box
      sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }}
    />
    <Typography variant="body2" noWrap>
      {label}
    </Typography>
  </Box>
);

/**
 * A send run's own status, with the per-bill breakdown behind it. One batch routinely
 * carries bills in different states, so the single status word is never the whole
 * answer — the counts say how many bills each state actually covers.
 */
export const ETaxBatchStatusCell = ({
  status,
  counts,
}: {
  status: ETaxStatus;
  counts: ETaxDocumentStatusCounts;
}) => (
  <Box>
    <StatusDot label={STATUS_LABEL[status]} color={STATUS_COLOR[status]} />
    <Typography variant="caption" color="text.secondary" display="block" noWrap>
      {presentCounts(counts)
        .map(({ label, value }) => `${label} ${numberWithCommas(value)}`)
        .join(" · ")}
    </Typography>
  </Box>
);

export const BillStatusDot = ({ status }: { status: string | null }) => {
  const { label, color } = getBillStatusMeta(status);
  return <StatusDot label={label} color={color} />;
};

export const EtaxStatusBadge = ({
  status,
  eligible,
  documentStatus,
  reasons = [],
  isPaper = false,
}: {
  status: ETaxDocumentStatus | null;
  eligible?: boolean | null;
  /** KTB's lifecycle status when it has reported one — it decides the wording. */
  documentStatus?: ETaxKtbDocumentStatus | null;
  reasons?: string[];
  /** A never-sent paper bill: shows กระดาษ instead of ค้างส่ง / ส่งไม่ได้. */
  isPaper?: boolean;
}) => {
  const { label, color } = getEtaxStatusMeta(status, eligible, documentStatus, isPaper);
  const chip = (
    <Chip
      label={label}
      size="small"
      sx={{
        color,
        bgcolor: `${color}1A`,
        fontWeight: 500,
        borderRadius: "16px",
        height: 24,
        minWidth: 84,
      }}
    />
  );

  // A refused bill without its reasons is just a red word. Every rule it broke goes in the
  // tooltip, because the operator has to fix all of them before re-issuing — one per visit
  // to the shop is not a workable loop.
  if (reasons.length === 0 || (isPaper && !status)) return chip;

  return (
    <Tooltip
      arrow
      title={
        <Box component="ul" sx={{ m: 0, pl: 2 }}>
          {reasons.map((reason) => (
            <li key={reason}>{reason}</li>
          ))}
        </Box>
      }
    >
      <span>{chip}</span>
    </Tooltip>
  );
};
